import React from 'react';
import PropTypes from 'prop-types';
import {Set} from 'immutable';
import {Editor, EditorState, CompositeDecorator, Modifier, convertFromRaw} from 'draft-js';
import {ResizableBox} from 'react-resizable';
import 'react-resizable/css/styles.css';
import classnames from 'classnames';
import {camelCase} from 'lodash';

import {Chip, Icon, LoadingSpinner} from '@albert-io/atomic';
import {callAction} from 'client/framework';
import supplementStore from 'client/Supplement/SupplementStore';
import supplementActions from 'client/Supplement/SupplementActions';
import {SUPPLEMENT_TYPES} from 'client/Supplements/shared';
import {pushQueryParams} from 'client/history';
import {withProps} from 'lib/hocs/WithProps';
import makeConstants from 'lib/makeConstants';
import {supplementModes} from 'client/Supplements/SupplementManager/SupplementManager.react';
import supplementManagerActions from 'client/Supplements/SupplementManager/SupplementManager.actions';
import {parse as parseSupplementString} from 'generic/MarkdownRendererV2/rules/supplementRules';

import {
  getNormalizedSelection,
  convertTextToRawContent,
  supplementStrategy,
  getEntityIndicesForBlock
} from './authoringTextInput.utils';

import './authoring-text-input.scss';

const eventTypes = {
  change: 'authoringTextInputChange',
  blur: 'authoringTextInputBlur'
};

const displayAsOptions = makeConstants('textarea', 'input');

export default withProps(
  () => ({
    supplements: supplementStore.getSupplements()
  }),
  class AuthoringTextInput extends React.Component {
    static propTypes = {
      className: PropTypes.string,
      displayAs: PropTypes.oneOf(Object.values(displayAsOptions)),
      error: PropTypes.string,
      height: PropTypes.number,
      label: PropTypes.string,
      name: PropTypes.string,
      onBlur: PropTypes.func,
      onChange: PropTypes.func,
      supplements: PropTypes.instanceOf(Set),
      value: PropTypes.string
    };

    static defaultProps = {
      displayAs: displayAsOptions.textarea,
      isFocused: false,
      height: 180,
      name: '',
      value: ''
    };

    constructor(props) {
      super();
      const initialContent = convertTextToRawContent(props);
      this.decorator = new CompositeDecorator([
        {
          strategy: supplementStrategy,
          component: Supplement
        }
      ]);
      this.state = {
        editorState: EditorState.createWithContent(convertFromRaw(initialContent), this.decorator),
        isFocused: false
      };
    }

    componentDidMount() {
      this.editorRef.editor.addEventListener('copy', this.handleCutCopy);
      this.editorRef.editor.addEventListener('cut', this.handleCutCopy);
    }

    /**
     * @todo: Migrate to getDerivedStateFromProps
     */
    UNSAFE_componentWillReceiveProps(nextProps) {
      // If the value is changing, and it's not what's currently in our input field
      const hasValueMismatch =
        this.props.value !== nextProps.value && nextProps.value !== this.value;
      // Or the contents of the supplements have changed (this happens when the supplements are edited)
      const hasSupplementMismatch =
        this.props.supplements.size === nextProps.supplements.size &&
        !this.props.supplements.equals(nextProps.supplements);
      if (hasValueMismatch || hasSupplementMismatch) {
        logger.warn(
          'Authoring text input value mismatch. This is likely ' +
            'due to adding or editing a supplement. Rebuilding editorState.'
        );
        const newState = convertTextToRawContent(nextProps);
        this.setState({
          editorState: EditorState.createWithContent(convertFromRaw(newState), this.decorator)
        });
      }
    }

    getDataProps() {
      return Object.entries(this.props).reduce((acc, [key, value]) => {
        if (key.startsWith('data-')) {
          const normalizedKey = camelCase(key.replace('data-', ''));
          acc[normalizedKey] = value;
        }
        return acc;
      }, {});
    }

    /**
     * There methods are getters so that value can be accessed
     * from the eventlike object as e.target[value/dataset/name/etc]
     */
    get value() {
      return this.state.editorState.getCurrentContent().getPlainText();
    }

    get dataset() {
      return this.getDataProps();
    }

    get name() {
      return this.props.name;
    }

    get selectionStart() {
      return getNormalizedSelection(this.state.editorState).selectionStart;
    }

    get selectionEnd() {
      return getNormalizedSelection(this.state.editorState).selectionEnd;
    }

    makeEventlike(type) {
      return {
        target: this,
        type
      };
    }

    getEditorStateData(editorState = this.state.editorState) {
      const content = editorState.getCurrentContent();
      const selection = editorState.getSelection();
      const focusKey = selection.getFocusKey();
      const focusOffset = selection.getFocusOffset();
      const anchorKey = selection.getFocusKey();
      const anchorOffset = selection.getFocusOffset();
      const startKey = selection.getStartKey();
      const startOffset = selection.getStartOffset();
      const endKey = selection.getEndKey();
      const endOffset = selection.getEndOffset();
      const blocksAsArray = content.getBlocksAsArray();
      return {
        content,
        selection,
        focusKey,
        focusOffset,
        anchorKey,
        anchorOffset,
        startKey,
        endKey,
        startOffset,
        endOffset,
        blocksAsArray
      };
    }

    handleCutCopy = (e) => {
      const {content, selection, startOffset, endOffset, startKey, endKey, blocksAsArray} =
        this.getEditorStateData();
      const startBlock = content.getBlockForKey(startKey);
      const endBlock = content.getBlockForKey(endKey);
      const startBlockIndex = blocksAsArray.indexOf(startBlock);
      const endBlockIndex = blocksAsArray.indexOf(endBlock);
      const selectedBlocks = blocksAsArray.slice(startBlockIndex, endBlockIndex + 1);
      if (
        selection.isCollapsed() || // There's no selection, so bail and let the browser handle the no-op
        [startBlockIndex, endBlockIndex].includes(-1) // We're in a bad state, bail and let the browser handle copying
      ) {
        return;
      }
      const copiedText = selectedBlocks
        .map((block, i, arr) => {
          const text = block.getText();
          if (startBlockIndex === endBlockIndex) {
            /**
             * If we're here, we're only copying across one block.
             * Otherwise, we're copying across multiple blocks, so we'll take the contents
             * of the first block starting from the startOffset, the entire contents of any
             * that are between our starting block and our ending block, and finally,
             * we'll take the substring of the last block up until the endOffset.
             */
            return selectedBlocks[0].getText().substring(startOffset, endOffset);
          }
          if (i === 0) {
            return text.substring(startOffset);
          }
          if (i + 1 === arr.length) {
            return text.substring(0, endOffset);
          }
          return text;
        })
        .join('\n');
      if (copiedText === '') {
        return;
      }
      e.preventDefault();
      e.clipboardData.setData('text/plain', copiedText);
    };

    handleChange = (editorState) => {
      const {selection, content, focusKey, focusOffset, anchorKey, anchorOffset} =
        this.getEditorStateData(editorState);
      const focusBlock = content.getBlockForKey(focusKey);
      const anchorBlock = content.getBlockForKey(anchorKey);
      const focusEntityKey = focusBlock.getEntityAt(focusOffset);
      const anchorEntityKey = anchorBlock.getEntityAt(anchorOffset);

      /**
       * If we're arrowing into the range of a supplement, we update the cursor to jump to the
       * end or beginning, depending on which direction the user is arrowing.
       */
      const isEditingText =
        this.state.editorState.getCurrentContent().getPlainText() !== content.getPlainText();
      const handledSelection = isEditingText
        ? null
        : [
            {
              name: 'focusOffset',
              entityKey: focusEntityKey,
              offset: focusOffset,
              block: focusBlock
            },
            {
              name: 'anchorOffset',
              entityKey: anchorEntityKey,
              offset: anchorOffset,
              block: anchorBlock
            }
          ].reduce((acc, {name, entityKey, offset, block}) => {
            if (entityKey === null) {
              return acc;
            }
            const updatedSelection = acc || selection;
            const blockEntityIndices = getEntityIndicesForBlock(block);
            const matchingEntityIndices = blockEntityIndices.find(
              ({startIndex, endIndex}) => startIndex <= offset && offset <= endIndex
            );
            const entityStart = matchingEntityIndices.startIndex;
            const entityEnd = matchingEntityIndices.endIndex;
            if (offset === entityStart + 1) {
              return updatedSelection.set(name, entityEnd);
            }
            if (offset === entityEnd - 1) {
              return updatedSelection.set(name, entityStart);
            }
            return acc;
          }, null);

      if (handledSelection) {
        // If we manually updated our selection, we update the selection object and force our component to rerender
        editorState = EditorState.forceSelection(editorState, handledSelection);
      }

      this.setState({editorState}, () => {
        if (this.props.onChange && this.props.value !== this.value) {
          this.props.onChange(this.makeEventlike(eventTypes.change));
        }
      });
    };

    handleReturn = () => {
      if (this.props.displayAs === displayAsOptions.input) {
        return 'handled';
      }
      return 'not-handled';
    };

    handlePaste = (text) => {
      if (this.props.displayAs === displayAsOptions.input) {
        this.handleChange(
          EditorState.push(
            this.state.editorState,
            Modifier.replaceText(
              this.state.editorState.getCurrentContent(),
              this.state.editorState.getSelection(),
              text.replace(/\n/g, ' ')
            )
          )
        );
        return 'handled';
      }
      return 'not-handled';
    };

    handleBlur = () => {
      this.setState({isFocused: false});
      if (!this.props.onBlur) {
        return;
      }
      const eventlike = this.makeEventlike(eventTypes.blur);
      this.props.onBlur(eventlike);
    };

    focusEditor = (e) => {
      if (e.target === e.currentTarget && !this.state.isFocused) {
        this.editorRef.editor.focus();
      }
    };

    makeRef = (instance) => {
      this.editorRef = instance;
    };

    render() {
      let {label, error} = this.props;
      const {className, displayAs} = this.props;
      label = label ? <div className='a-form-input__label'>{label}</div> : null;
      error = error ? <div className='a-form-input__error'>{error}</div> : null;
      const isTextArea = displayAs === displayAsOptions.textarea;
      const EditorWrapper = isTextArea ? ResizableBox : 'div';
      const editorWrapperProps = isTextArea
        ? {
            axis: 'y',
            handleSize: [10, 10],
            height: this.props.height,
            maxConstraints: [Infinity, Infinity],
            minConstraints: [Infinity, 20],
            width: Infinity
          }
        : {};
      return (
        <div className={classnames('authoring-text-editor-input a-form-input', className)}>
          {label}
          <EditorWrapper {...editorWrapperProps}>
            <div
              className={classnames('authoring-text-editor-input__input', {
                'authoring-text-editor-input__input--focused': this.state.isFocused,
                'authoring-text-editor-input__input--input': displayAs === displayAsOptions.input,
                'authoring-text-editor-input__input--textarea':
                  displayAs === displayAsOptions.textarea
              })}
              onClick={this.focusEditor}
            >
              <Editor
                spellCheck
                stripPastedStyles
                ref={this.makeRef}
                editorState={this.state.editorState}
                onChange={this.handleChange}
                handleReturn={this.handleReturn}
                onLeftArrow={this.onLeftArrow}
                onRightArrow={this.onRightArrow}
                onUpArrow={this.onUpArrow}
                onDownArrow={this.onDownArrow}
                onBlur={this.handleBlur}
                supplementChangeId={supplementStore.getChangeId()}
              />
            </div>
          </EditorWrapper>
          {error}
        </div>
      );
    }
  },
  {
    suppressPureComponentWarning: true
  }
);

class Supplement extends React.Component {
  static propTypes = {
    decoratedText: PropTypes.string,
    offsetKey: PropTypes.string
  };

  constructor(props) {
    super();
    const supplement = parseSupplementString(props.decoratedText);
    this.state = {
      supplement,
      isReady: supplementStore.hasSupplement(supplement.id),
      hasError: false
    };
  }

  componentDidMount = () => {
    if (!this.state.isReady) {
      callAction(supplementActions.FETCH_SUPPLEMENT, this.state.supplement.id);
      supplementStore
        .getFetchSupplementPromise()
        .then(() => {
          this.setState({
            isReady: true
          });
        })
        .catch((/* err */) => {
          // if(err.status === 404) // TODO: different error message for different status?
          this.setState({hasError: true});
        });
    }
  };

  openSupplementInEditMode = () => {
    global.document.activeElement.blur();
    pushQueryParams({
      supplementMode: supplementModes.EDIT_ACTIVE,
      supplement: this.state.supplement.id
    });
    callAction(supplementManagerActions.SHOW_MODAL, true);
  };

  handleClick = (e) => {
    e.preventDefault();
    this.openSupplementInEditMode();
  };

  handleKeyDown = (e) => {
    if (['Enter', ' '].includes(e.key)) {
      e.preventDefault();
      e.stopPropagation();
      this.openSupplementInEditMode();
    }
  };

  render() {
    if (!this.state.isReady && !this.state.hasError) {
      return (
        <Chip>
          <LoadingSpinner />
        </Chip>
      );
    }
    if (this.state.hasError) {
      return (
        <div className='u-display_flex'>
          <Chip error>This supplement no longer exists</Chip>
          {/* eslint-disable-next-line react/button-has-type */}
        </div>
      );
    }

    const supplement = supplementStore.getSupplement(this.state.supplement.id);

    const icon = {
      [SUPPLEMENT_TYPES.IMAGE]: 'image',
      [SUPPLEMENT_TYPES.PASSAGE]: 'file-alt',
      [SUPPLEMENT_TYPES.AUDIO]: 'volume-up',
      [SUPPLEMENT_TYPES.VIDEO]: 'video',
      [SUPPLEMENT_TYPES.SORTABLE_TABLE]: 'table',
      [SUPPLEMENT_TYPES.FREE_FORM]: null
    }[supplement.getType()];

    let contents = supplement.getName();
    /**
     * If the supplement is `viewable` or `downloadable`, we alter the displayed contents.
     */
    if (this.state.supplement.attributes.viewable) {
      contents += ' (Viewable)';
    } else if (this.state.supplement.attributes.downloadable) {
      contents += ' (Download)';
    }
    return (
      <Chip
        as='button'
        onClick={this.handleClick}
        onKeyDown={this.handleKeyDown}
        data-offset-key={this.props.offsetKey}
        data-supplement-contents={contents}
        className='authoring-text-editor__supplement'
      >
        <span
          className='authoring-text-editor__supplement-uuid'
          data-offset-key={this.props.offsetKey}
        >
          {this.props.children}
        </span>
        <Icon className='u-mar-l_1' icon={icon} />
      </Chip>
    );
  }
}
